import MomentUtils from "@date-io/moment";
import { Dialog } from "@material-ui/core";
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import {
  Fragment,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { BiPlusCircle } from "react-icons/bi";
import { FaRegTrashAlt } from "react-icons/fa";
import { LuChevronDown } from "react-icons/lu";
import { MdOutlineFileUpload } from "react-icons/md";
import { handleKeyDown } from "../../../../common/common";
import CustomShowModal from "../../../../components/Common/CustomShowModal";
import AddPortQueryModal from "../../../../components/Orders/AddPortQueryModal";
import {
  queryPortDisabledFields,
  refreshCanalOptions,
  termsCoEfficientTable,
} from "../../../../constants";

/**
 * CargoDetails – mirrors the "Update Cargo" / AddNewOrder layout.
 *
 * Edits are kept in a local draft until the user clicks Save
 * (triggered by parent via ref.handleSaveRequest()).
 * A confirmation dialog warns that changes will affect all 3 tables.
 *
 * Props:
 *   calculatorData   – full calculator state (source of truth from parent)
 *   onSave           – (draftData) => void   called after confirm
 */
const CargoDetails = forwardRef((props, ref) => {
  const {
    calculatorData = {},
    activeSide = "customerCargo",
    onSave = () => { },
  } = props;

  // ── Local draft – all edits go here, not to parent until Save ────────────
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(calculatorData || {})));
  const [showConfirm, setShowConfirm] = useState(false);
  const [portModal, setPortModal] = useState(false);
  const [selectedPort, setSelectedPort] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const fileInputRef = useRef(null);

  // Sync draft when parent data changes (e.g. initial load)
  useEffect(() => {
    setDraft(JSON.parse(JSON.stringify(calculatorData || {})));
  }, [calculatorData?._id]); // only re-sync on record change, not every keystroke

  // ── Expose handleSaveRequest to parent ───────────────────────────────────
  useImperativeHandle(ref, () => ({
    handleSaveRequest: () => {
      const originalPorts = calculatorData?.vesselVoyageData || [];
      const currentPorts = draft?.vesselVoyageData || [];

      let isPortChanged = false;
      if (originalPorts.length !== currentPorts.length) {
        isPortChanged = true;
      } else {
        for (let i = 0; i < originalPorts.length; i++) {
          if (
            originalPorts[i]?.operation !== currentPorts[i]?.operation ||
            originalPorts[i]?.port !== currentPorts[i]?.port
          ) {
            isPortChanged = true;
            break;
          }
        }
      }

      if (isPortChanged) {
        setShowConfirm(true);
      } else {
        onSave(draft);
      }
    },
  }));

  const { vesselVoyageData = [], customerCargo = {}, vendorCargo = {} } = draft || {};
  const portList = vesselVoyageData || [];

  const activeCargo = activeSide === "vendorCargo" ? vendorCargo : customerCargo;

  const name = activeCargo?.name || "";
  const freightMode = activeSide === "vendorCargo"
    ? (draft?.freightModeExpense || "Single Freight")
    : (draft?.freightModeRevenue || "Single Freight");
  const qtyMode = activeCargo?.qtyMode || "MOLOO";
  const laycanStartDate = activeCargo?.laycanStartDate || null;
  const laycanEndDate = activeCargo?.laycanEndDate || null;
  const message = activeCargo?.message || "";
  const showMinMax = qtyMode === "MINMAX";

  // ── Helpers ───────────────────────────────────────────────────────────────
  const inputClass =
    "w-full border border-gray-300 text-[13px] rounded-[3px] px-1.5 py-0 m-0 box-border block outline-none focus:border-[#313C4C] focus:ring-1 focus:ring-[#313C4C]/20 text-gray-800 bg-white disabled:bg-gray-300 shadow-sm transition-all placeholder:text-gray-300";

  // Fields that live in customerCargo/vendorCargo cargoList rows
  const CARGO_LIST_FIELDS = new Set([
    "rate", "terms", "turnTime", "freight",
    "adcom", "other", "brokerage", "account",
    "cargoQty", "tolerance",
  ]);

  const onChangePortRow = (index, e) => {
    const { name: fieldName, value, type } = e.target;
    const finalValue = type === "number" ? Number(value) : value;

    const newVoyage = [...(draft?.vesselVoyageData || [])];
    const newCustList = [...(customerCargo?.cargoList || [])];
    const newVendList = [...(vendorCargo?.cargoList || [])];

    if (fieldName === "operation") {
      newVoyage[index] = { ...newVoyage[index], operation: finalValue };
      if (newCustList[index]) newCustList[index] = { ...newCustList[index], operation: finalValue };
      if (newVendList[index]) newVendList[index] = { ...newVendList[index], operation: finalValue };
    } else if (CARGO_LIST_FIELDS.has(fieldName)) {
      if (activeSide === "customerCargo") {
        if (newCustList[index]) newCustList[index] = { ...newCustList[index], [fieldName]: finalValue };
      } else {
        if (newVendList[index]) newVendList[index] = { ...newVendList[index], [fieldName]: finalValue };
      }
    } else {
      newVoyage[index] = { ...newVoyage[index], [fieldName]: finalValue };
    }

    setDraft({
      ...draft,
      vesselVoyageData: newVoyage,
      customerCargo: { ...customerCargo, cargoList: newCustList },
      vendorCargo: { ...vendorCargo, cargoList: newVendList },
    });
  };

  const onChangeConfig = (e) => {
    const { name: fieldName, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;

    if (fieldName === "freightMode") {
      const modeKey = activeSide === "vendorCargo" ? "freightModeExpense" : "freightModeRevenue";
      setDraft({ ...draft, [modeKey]: finalValue });
    } else {
      setDraft({
        ...draft,
        [activeSide]: { ...activeCargo, [fieldName]: finalValue },
      });
    }
  };

  const onChangeMessage = (e) => {
    setDraft({
      ...draft,
      [activeSide]: { ...activeCargo, message: e.target.value },
    });
  };

  const onChangeDate = (fieldName, date) => {
    setDraft({
      ...draft,
      [activeSide]: { ...activeCargo, [fieldName]: date },
    });
  };

  // Add port row (inside draft)
  const defaultVoyageRow = (operation = "Discharging") => ({
    operation, port: "", min: 0, max: 0, cargo: "", stowFactor: 0,
    laycanStartDate: null, laycanEndDate: null, actualCargoQty: 0,
    arrivalTime: null, departureTime: null, portStay: 0,
  });
  const defaultCargoRow = (operation = "Discharging") => ({
    operation, port: "", rate: 0, terms: "SHINC", factor: 1, turnTime: 0,
    freight: 0, adcom: 0, other: 0, brokerage: 0, account: "",
    demurrageRate: 0, despatchRate: 0, additionalRevenue: 0,
    additionalExpense: 0, laytimeIncurred: "",
  });

  const onAddPortDraft = (afterIndex) => {
    const newVoyage = [...(draft?.vesselVoyageData || [])];
    const newCustList = [...(customerCargo?.cargoList || [])];
    const newVendList = [...(vendorCargo?.cargoList || [])];
    const insertAt = typeof afterIndex === "number" ? afterIndex + 1 : newVoyage.length;
    newVoyage.splice(insertAt, 0, defaultVoyageRow());
    newCustList.splice(insertAt, 0, defaultCargoRow());
    newVendList.splice(insertAt, 0, defaultCargoRow());
    setDraft({
      ...draft,
      vesselVoyageData: newVoyage,
      customerCargo: { ...customerCargo, cargoList: newCustList },
      vendorCargo: { ...vendorCargo, cargoList: newVendList },
    });
  };

  const onDeletePortDraft = (index) => {
    setDraft({
      ...draft,
      vesselVoyageData: (draft?.vesselVoyageData || []).filter((_, i) => i !== index),
      customerCargo: {
        ...customerCargo,
        cargoList: (customerCargo?.cargoList || []).filter((_, i) => i !== index),
      },
      vendorCargo: {
        ...vendorCargo,
        cargoList: (vendorCargo?.cargoList || []).filter((_, i) => i !== index),
      },
    });
  };

  // Port selection modal
  const onSelectPort = (item, index) => {
    setSelectedPort({ port: item.port || "", lat: item.lat, lng: item.lng });
    setSelectedIndex(index);
    setPortModal(true);
  };

  const onSavePort = (portName, val) => {
    const newVoyage = [...(draft?.vesselVoyageData || [])];
    newVoyage[selectedIndex] = { ...newVoyage[selectedIndex], port: portName, lat: val?.lat, lng: val?.lng };
    const newCustList = [...(customerCargo?.cargoList || [])];
    const newVendList = [...(vendorCargo?.cargoList || [])];
    if (newCustList[selectedIndex]) newCustList[selectedIndex] = { ...newCustList[selectedIndex], port: portName };
    if (newVendList[selectedIndex]) newVendList[selectedIndex] = { ...newVendList[selectedIndex], port: portName };
    setDraft({
      ...draft,
      vesselVoyageData: newVoyage,
      customerCargo: { ...customerCargo, cargoList: newCustList },
      vendorCargo: { ...vendorCargo, cargoList: newVendList },
    });
    setPortModal(false);
    setSelectedPort({});
    setSelectedIndex(0);
  };

  const onClosePortModal = () => {
    setPortModal(false);
    setSelectedPort({});
    setSelectedIndex(0);
  };

  // Confirm save
  const handleConfirmSave = () => {
    setShowConfirm(false);
    onSave(draft);
  };

  // Table styling matching dark header with white text
  const thClass = "px-1.5 py-0.5 text-center border-r border-b border-[#334155] text-[11px] font-medium text-white bg-[#1e293b] capitalize tracking-wider align-middle";
  const tdClass = "border-r border-b border-slate-300 p-0 align-middle bg-white";
  const tableInputClass = "w-full h-[19px] text-[11px] px-1.5 py-0 m-0 box-border block outline-none focus:bg-white focus:hover:bg-white [&:focus:hover]:bg-white focus:ring-1 focus:ring-inset focus:ring-[#2563eb] bg-[#fef9c3] hover:bg-[#fef08a] text-slate-900 disabled:bg-[#f1f5f9] disabled:text-[#94a3b8] disabled:cursor-not-allowed rounded-none border-none placeholder:text-gray-400 font-medium transition-colors";

  return (
    <Fragment>
      <div className="w-full bg-white flex flex-col font-sans">

        {/* ── CARGO OPERATIONS ─────────────────────────────────────────────── */}
        <div className="flex justify-between items-end mb-0.5">
          <h3 className="text-[11px] font-medium text-slate-900 capitalize tracking-wider">
            Cargo Operations
          </h3>
        </div>

        <div className="overflow-x-auto scroll-thin border-t border-l border-slate-300 rounded-none">
          <table className="w-full text-left border-collapse whitespace-nowrap">
            <thead className="bg-[#f0f2f5] text-slate-900 text-[11px] capitalize">
              <tr>
                <th className={thClass} style={{ width: "36px", minWidth: "36px" }}>Add</th>
                <th className={thClass} style={{ width: "90px", minWidth: "105px" }}>Operation</th>
                <th className={thClass} style={{ width: "120px", minWidth: "80px" }}>Port</th>
                {showMinMax ? (
                  <>
                    <th className={thClass} style={{ width: "60px" }}>Min</th>
                    <th className={thClass} style={{ width: "60px" }}>Max</th>
                  </>
                ) : (
                  <>
                    <th className={thClass} style={{ width: "60px" }}>Cargo Qty</th>
                    <th className={thClass} style={{ width: "50px" }}>Tolerance(%)</th>
                  </>
                )}
                <th className={thClass} style={{ width: "60px" }}>Rate MT/Day</th>
                <th className={thClass} style={{ width: "107px", minWidth: "107px" }}>Terms</th>
                <th className={thClass} style={{ width: "50px" }}>Turn Time(D)</th>
                <th className={thClass} style={{ width: "60px" }}>Freight</th>
                <th className={thClass} style={{ width: "50px" }}>Adcom %</th>
                <th className={thClass} style={{ width: "50px" }}>Other %</th>
                <th className={thClass} style={{ width: "50px" }}>Brokerage %</th>
                <th className={thClass} style={{ width: "80px" }}>Counter Party</th>
                <th className={thClass} style={{ width: "70px" }}>Cargo Name</th>
                <th className={thClass} style={{ width: "60px" }}>Stow Factor</th>
                <th className={thClass} style={{ width: "20px" }}></th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {portList.map((item, index) => {
                const operation = item?.operation || "";
                const port = item?.port || "";
                const activeRow = activeCargo?.cargoList?.[index] || {};
                const cargoQty = activeRow.cargoQty ?? item.actualCargoQty ?? 0;
                const tolerance = activeRow.tolerance ?? 0;
                const min = item.min ?? 0;
                const max = item.max ?? 0;
                const rate = activeRow.rate ?? 0;
                const terms = activeRow.terms ?? "SHINC";
                const turnTime = activeRow.turnTime ?? 0;
                const freight = activeRow.freight ?? 0;
                const adcom = activeRow.adcom ?? 0;
                const other = activeRow.other ?? 0;
                const brokerage = activeRow.brokerage ?? 0;
                const account = activeRow.account ?? "";
                const cargo = item.cargo ?? "";
                const stowFactor = item.stowFactor ?? 0;

                const firstLoadingIndex = portList.findIndex(
                  (op) => op?.operation?.toLowerCase() === "loading"
                );
                const disableForSingleFreight =
                  freightMode === "Single Freight" && index !== firstLoadingIndex;

                return (
                  <tr className="hover:bg-[#f8fafc] transition-colors group" key={index}>
                    {/* Add */}
                    <td className={tdClass}>
                      <div className="flex items-center justify-center w-full h-[19px]">
                        <BiPlusCircle
                          size={16}
                          className="cursor-pointer text-[#313C4C] hover:text-[#252e3b]"
                          onClick={() => onAddPortDraft(index)}
                        />
                      </div>
                    </td>

                    {/* Operation */}
                    <td className={tdClass}>
                      <div className="relative">
                        <select
                          disabled={index < 1}
                          value={operation}
                          name="operation"
                          className={`${tableInputClass} appearance-none pr-5`}
                          onChange={(e) => onChangePortRow(index, e)}
                        >
                          <option value="Commence" disabled hidden>Commence</option>
                          <option value="Loading">Loading</option>
                          <option value="Discharging">Discharging</option>
                        </select>
                        <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-slate-500 text-xs">
                          <LuChevronDown />
                        </span>
                      </div>
                    </td>

                    {/* Port */}
                    <td className={tdClass}>
                      <div
                        className={`${tableInputClass} flex items-center justify-between cursor-pointer`}
                        onClick={() => onSelectPort(item, index)}
                      >
                        <span className="truncate">{port || (
                          <span className="text-gray-400">Select Port</span>
                        )}</span>
                      </div>
                    </td>

                    {/* Cargo Qty / Tolerance or Min / Max */}
                    {(showMinMax ? [min, max] : [cargoQty, tolerance]).map((val, i) => (
                      <td key={i} className={tdClass}>
                        <input
                          type="number"
                          onKeyDown={handleKeyDown}
                          value={val}
                          name={showMinMax ? (i === 0 ? "min" : "max") : (i === 0 ? "cargoQty" : "tolerance")}
                          disabled={queryPortDisabledFields?.[operation]?.[showMinMax ? (i === 0 ? "min" : "max") : (i === 0 ? "cargoQty" : "tolerance")]}
                          onChange={(e) => onChangePortRow(index, e)}
                          className={tableInputClass}
                          placeholder="0"
                        />
                      </td>
                    ))}

                    {/* Rate, Terms, TurnTime, Freight, Adcom, Other, Brokerage */}
                    {[
                      { name: "rate", value: rate },
                      { name: "terms", value: terms },
                      { name: "turnTime", value: turnTime },
                      { name: "freight", value: freight },
                      { name: "adcom", value: adcom },
                      { name: "other", value: other },
                      { name: "brokerage", value: brokerage },
                    ].map((field, i) =>
                      field.name === "terms" ? (
                        <td key={i} className={tdClass}>
                          <div className="relative">
                            <select
                              name="terms"
                              value={field.value}
                              onChange={(e) => onChangePortRow(index, e)}
                              className={`${tableInputClass} pr-5 appearance-none`}
                            >
                              {termsCoEfficientTable.map((opt, idx) => (
                                <option key={idx} value={opt.label}>{opt.label}</option>
                              ))}
                            </select>
                            <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-slate-500 text-xs">
                              <LuChevronDown />
                            </span>
                          </div>
                        </td>
                      ) : (
                        <td key={i} className={tdClass}>
                          <input
                            type="number"
                            onKeyDown={handleKeyDown}
                            value={field.value}
                            name={field.name}
                            disabled={
                              queryPortDisabledFields?.[operation]?.[field.name] ||
                              (["freight", "adcom", "other", "brokerage"].includes(field.name) && disableForSingleFreight)
                            }
                            onChange={(e) => onChangePortRow(index, e)}
                            className={tableInputClass}
                            placeholder="0"
                          />
                        </td>
                      )
                    )}

                    {/* Counter Party, Cargo Name, Stow Factor */}
                    {[
                      { name: "account", value: account },
                      { name: "cargo", value: cargo },
                      { name: "stowFactor", value: stowFactor, type: "number" },
                    ].map((field, i) => (
                      <td key={i} className={tdClass}>
                        <input
                          type={field.type || "text"}
                          value={field.value}
                          name={field.name}
                          disabled={queryPortDisabledFields?.[operation]?.[field.name]}
                          onChange={(e) => onChangePortRow(index, e)}
                          className={tableInputClass}
                          placeholder="—"
                        />
                      </td>
                    ))}

                    {/* Delete */}
                    <td className={tdClass}>
                      <div className="flex items-center justify-center w-full h-[19px]">
                        {index > 0 && (
                          <FaRegTrashAlt
                            className="text-rose-500 cursor-pointer hover:text-rose-700"
                            onClick={() => onDeletePortDraft(index)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── CONFIGURATION + REMARKS ──────────────────────────────────────── */}
        <div className="flex-col flex md:flex-row gap-2 py-2 border-t border-gray-100 bg-white">
          {/* Configuration Panel */}
          <div className="flex flex-col gap-1.5 bg-[#f8fafc] border border-slate-200 rounded-none p-2 shadow-2xs w-full md:w-[40%]">
            <h3 className="text-[11px] font-medium text-slate-900 capitalize tracking-wider border-b border-slate-200 pb-0.5 mb-0">
              Configuration
            </h3>

            <div className="flex flex-col gap-0.5">
              <label className="text-[11px] font-medium text-slate-900 capitalize tracking-wider">Name</label>
              <input
                className="w-full text-[11px] px-2 py-0.5 border border-slate-300 rounded-none focus:border-slate-800 outline-none text-slate-900 bg-white shadow-2xs h-6.5"
                placeholder="e.g. Wheat Cargo Q1"
                value={name}
                name="name"
                onChange={onChangeConfig}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-0.5">
                <label className="text-[11px] font-medium text-slate-900 capitalize tracking-wider">Freight Mode</label>
                <select
                  value={freightMode}
                  onChange={onChangeConfig}
                  name="freightMode"
                  className="w-full text-[11px] px-2 py-0.5 border border-slate-300 rounded-none focus:border-slate-800 outline-none text-slate-900 bg-white shadow-2xs h-6.5"
                >
                  <option value="Single Freight">Voyage (Single)</option>
                  <option value="Multiple Freight">Multiple Freight</option>
                </select>
              </div>
              <div className="flex flex-col gap-0.5">
                <label className="text-[11px] font-medium text-slate-900 capitalize tracking-wider">Qty Option</label>
                <select
                  value={qtyMode}
                  onChange={onChangeConfig}
                  name="qtyMode"
                  className="w-full text-[11px] px-2 py-0.5 border border-slate-300 rounded-none focus:border-slate-800 outline-none text-slate-900 bg-white shadow-2xs h-6.5"
                >
                  <option value="MOLOO">MOLOO</option>
                  <option value="MINMAX">MINMAX</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-0.5">
                <label className="text-[11px] font-medium text-slate-900 capitalize tracking-wider">Laycan Start</label>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <KeyboardDateTimePicker
                    autoOk inputVariant="outlined" format="DD-MM-YY HH:mm"
                    placeholder="dd-mm-yy hh:mm" value={laycanStartDate || null}
                    onChange={(date) => onChangeDate("laycanStartDate", date)}
                    className="custom-date-time-picker w-full bg-white shadow-2xs [&_.MuiOutlinedInput-adornedEnd]:pr-1"
                    size="small" InputProps={{ style: { fontSize: "11px" } }}
                    KeyboardButtonProps={{ style: { padding: 1 } }} disableMaskedInput={true}
                  />
                </MuiPickersUtilsProvider>
              </div>
              <div className="flex flex-col gap-0.5">
                <label className="text-[11px] font-medium text-slate-900 capitalize tracking-wider">Laycan End</label>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <KeyboardDateTimePicker
                    autoOk inputVariant="outlined" format="DD-MM-YY HH:mm"
                    placeholder="dd-mm-yy hh:mm" value={laycanEndDate || null}
                    onChange={(date) => onChangeDate("laycanEndDate", date)}
                    className="custom-date-time-picker w-full bg-white shadow-2xs [&_.MuiOutlinedInput-adornedEnd]:pr-1"
                    size="small" InputProps={{ style: { fontSize: "11px" } }}
                    KeyboardButtonProps={{ style: { padding: 1 } }} disableMaskedInput={true}
                  />
                </MuiPickersUtilsProvider>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {refreshCanalOptions.map((item, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    id={`cd-${item.value}`}
                    name={item.value}
                    checked={!!draft?.[item.value]}
                    onChange={onChangeConfig}
                    className="h-3.5 w-3.5"
                  />
                  <label htmlFor={`cd-${item.value}`} className="cursor-pointer text-[11px] font-medium text-slate-900">
                    {item.label}
                  </label>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-0.5 mt-0">
              <label className="text-[11px] font-medium text-slate-900 capitalize tracking-wider">Attachments</label>
              <div
                className="border border-dashed border-slate-400 bg-white rounded-none flex justify-center items-center cursor-pointer hover:bg-slate-50 transition-colors py-1.5"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="flex items-center gap-1.5">
                  <MdOutlineFileUpload size={14} className="text-slate-700" />
                  <span className="text-[11px] font-medium text-slate-900">Click or drop to upload files</span>
                </div>
                <input type="file" ref={fileInputRef} className="hidden" multiple />
              </div>
            </div>
          </div>

          {/* Remarks */}
          <div className="flex flex-col gap-1.5 bg-[#f8fafc] border border-slate-200 rounded-none p-2 shadow-2xs w-full md:w-[60%]">
            <h3 className="text-[11px] font-medium text-slate-900 capitalize tracking-wider border-b border-slate-200 pb-0.5 mb-0">
              Remarks / Notes
            </h3>
            <textarea
              className="w-full flex-1 min-h-[185px] p-2 text-[11px] border border-slate-300 rounded-none focus:border-slate-800 outline-none text-slate-900 bg-white resize-none shadow-2xs leading-relaxed"
              placeholder="Enter internal notes, special instructions, or terms here..."
              value={message}
              name="message"
              onChange={onChangeMessage}
            />
          </div>
        </div>
      </div>

      {/* ── Port search modal ─────────────────────────────────────────────── */}
      <CustomShowModal
        open={portModal}
        handleClose={onClosePortModal}
        maxWidth="xs"
        disableClose
        title="Change Port"
      >
        <AddPortQueryModal
          selectedPort={selectedPort}
          setSelectedPort={setSelectedPort}
          onSavePortOperationsOfQuery={onSavePort}
        />
      </CustomShowModal>

      {/* ── Confirmation dialog ───────────────────────────────────────────── */}
      <Dialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        maxWidth="xs"
        PaperProps={{
          style: {
            borderRadius: "6px",
            border: "1px solid #cbd5e1",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            width: "360px",
          },
        }}
      >
        <div className="p-4 flex flex-col items-center text-center font-sans">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-2">
            <svg className="w-5 h-5 text-[#313C4C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>

          <h3 className="text-[14px] font-semibold text-slate-900 mb-1.5">Confirm Port Changes</h3>

          <p className="text-[11px] text-slate-600 mb-3.5 leading-normal px-1">
            These changes will be applied to the
            <span className="font-semibold text-slate-800"> Port Operations</span>,
            <span className="font-semibold text-slate-800"> Revenue</span>, and
            <span className="font-semibold text-slate-800"> Expense</span> tables.
            We will also run a quick recalculation to update your totals.
          </p>

          <div className="flex gap-2 w-full">
            <button
              onClick={() => setShowConfirm(false)}
              className="flex-1 h-7 px-3 rounded-none border border-slate-300 text-slate-700 text-[11px] font-medium hover:bg-slate-50 transition-colors focus:outline-none cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmSave}
              className="flex-1 h-7 px-3 rounded-none bg-[#313C4C] text-white text-[11px] font-medium hover:bg-[#252e3b] transition-colors shadow-2xs focus:outline-none cursor-pointer"
            >
              Save & Recalculate
            </button>
          </div>
        </div>
      </Dialog>
    </Fragment>
  );
});

export default CargoDetails;
